import React from 'react'
import Input from '@components/forms/input'
import { IValueEditorProps } from '@app-types/general'
import useValueEditor from '@hooks/useValueEditor'
import { createLogger } from '@utils/logger'

// Initialize logger
const log = createLogger('NumericEditor', { prependScope: true })
log.setLevel('info')

// Note: We convert the value into a string to allow for empty values
// (null is not a valid value for an input field)
// This way, we can use parseFloat to convert the string to a number when needed

type NumericEditorProps = Omit<IValueEditorProps, 'value' | 'onSave' | 'onChange' | 'parseValue' | 'serializeValue'> & {
  value?: string | number | null
  onSave?: (value: number | null) => void
  onChange?: (data: { name?: string; value: number | null }) => void
  placeholder?: string
  parseValue?: (value: string | number | null) => string
  serializeValue?: (value: string) => number | null
}

type ChangeEvent = {
  name: string
  value: string
}

const defaultParseValueFn = (value: string | number | null): string => {
  return value === null ? '' : String(value)
}

const defaultSerializeValueFn = (value: string): number | null => {
  return value === '' ? null : parseFloat(value)
}

function convertToNumber(value: string | number | null): number | null {
  if (value === null) return null
  if (typeof value === 'number') return value
  const parsed = parseFloat(value)
  return Number.isNaN(parsed) ? null : parsed
}

const NumericEditor: React.FC<NumericEditorProps> & { EDITOR_TYPE: 'numeric' } = ({
  name = '',
  value = null,
  onSave,
  onChange,
  onEditCancel,
  placeholder = '',
  parseValue = defaultParseValueFn,
  serializeValue = defaultSerializeValueFn,
  ...restProps
}) => {
  // Convert external value to number | null so that onSave matches what is expected
  const externalNumericValue = convertToNumber(value)

  const { localValue, handleLocalChange, handleBlurOrSave } = useValueEditor<number | null, string>({
    value: externalNumericValue,
    parseValue,
    serializeValue,
    onChange: onChange ? ({ value }) => onChange({ name, value }) : undefined,
    onSave,
    onEditCancel,
  })

  const handleBlur = () => {
    handleBlurOrSave()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      ;(event.target as HTMLInputElement).blur()
    }
  }

  const handleChange = (event: ChangeEvent) => {
    const newValue = event.value
    // Allow only valid numeric input
    if (/^[+-]?\d*\.?\d*$/.test(newValue) || newValue === '') {
      handleLocalChange(newValue)
    }
  }

  return (
    <span className="content-editor content-editor--numeric numeric-editor">
      <Input
        name={name}
        type="text" // Use "text" to support decimals and empty values
        inputMode="decimal" // Suggest numeric keyboard on mobile
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        {...restProps}
      />
    </span>
  )
}

// Add the static property to the component
NumericEditor.EDITOR_TYPE = 'numeric'

export default NumericEditor
